/**
 * A consumer package (e.g. product-synthai) resolves `@bloo-team/core-shell`
 * straight to this source file via package `exports`, so it compiles this
 * file inside its own TS program — one that loads its own ambient env.d.ts,
 * not core-shell's. The triple-slash reference makes the declaration travel
 * with the file into any program that compiles it, regardless of that
 * program's own `include`.
 */
// `import type` of a global-augmenting .d.ts is not loaded by a consumer's
// program the way a triple-slash reference is; this needs to be one.
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="./env.d.ts" />
import { useSyncExternalStore } from 'react'
import { createAuthApi, createClient, createHandoffApi } from '@bloo-team/api-client'
import { getSession } from './session'
import { signOutOnUnauthorized } from './auth'

/**
 * Vite-injected env, typed via `env.d.ts` (a local ambient declaration, not
 * `vite/client`) so this package typechecks standalone. Kept as an exact
 * `import.meta.env` expression — vitest's env-stub machinery only rewrites
 * contiguous reads of it, so routing this through an inline cast would
 * silently stop `vi.stubEnv` from reaching it in tests.
 */
const VITE_ENV: ImportMetaEnv = import.meta.env

/**
 * Where API calls go.
 *
 * `''` — the default, and what dev, demo and e2e all use — keeps every request
 * same-origin: vite proxies the six prefixes to BLOO_API_ORIGIN in dev.
 *
 * A DEPLOYED build sets VITE_API_BASE_URL to an absolute origin
 * (https://ap1.mira.bloo.io) and the console goes cross-origin. That works
 * because auth is an SSID *header*, not a cookie — there is no cookie to scope
 * across sites — but it does require CORS on the backend, including a
 * preflight answer for the non-safelisted SSID header
 * (docs/BACKEND-ASKS.md BA-D1).
 */
export const API_BASE_URL: string = VITE_ENV.VITE_API_BASE_URL ?? ''

/**
 * Whether demo mode exists in this build at all.
 *
 * On by default — dev, e2e and every non-deployed build keep the demo/live
 * toggle, and Constitution rule 6 holds. A DEPLOYED build sets
 * VITE_DEMO_MODE=off, because VITE_AUTH_MODE only seeds the *default* mode:
 * the toggle writes `bloo.authmode` to localStorage, so without this flag a
 * visitor could leave a real host sitting in demo mode looking at sample data.
 */
export const DEMO_MODE_AVAILABLE: boolean = VITE_ENV.VITE_DEMO_MODE !== 'off'

/**
 * Console-wide API client instance. A 401 anywhere drops the session and
 * lands on login.
 */
export const client = createClient({
  baseUrl: API_BASE_URL,
  getSsid: () => getSession()?.ssid ?? null,
  onUnauthorized: signOutOnUnauthorized,
})

export const authApi = createAuthApi(client)

/**
 * Auth mode is a runtime choice, switchable on the login page:
 *   live — real /lc/users/* calls (through the dev proxy / same origin)
 *   demo — built-in sample data (any credentials; demo-otp@ / fail@ special)
 * Persisted per browser. VITE_AUTH_MODE seeds the default only.
 */
export type AuthMode = 'live' | 'demo'

const MODE_KEY = 'bloo.authmode'
const modeListeners = new Set<() => void>()

function readMode(): AuthMode {
  if (!DEMO_MODE_AVAILABLE) return 'live'
  const stored = typeof window !== 'undefined' ? window.localStorage.getItem(MODE_KEY) : null
  if (stored === 'live' || stored === 'demo') return stored
  return VITE_ENV.VITE_AUTH_MODE === 'live' ? 'live' : 'demo'
}

let mode: AuthMode = readMode()

export function getAuthMode(): AuthMode {
  return mode
}

export function setAuthMode(next: AuthMode) {
  // Narrowed, not ignored: signInWithSsid (auth.ts) calls setAuthMode('live')
  // on the existing-session path and must keep working.
  const resolved: AuthMode = DEMO_MODE_AVAILABLE ? next : 'live'
  mode = resolved
  window.localStorage.setItem(MODE_KEY, resolved)
  modeListeners.forEach((cb) => cb())
}

export function useAuthMode(): AuthMode {
  return useSyncExternalStore(
    (cb) => {
      modeListeners.add(cb)
      return () => modeListeners.delete(cb)
    },
    () => mode,
  )
}

/** What "live" points at — shown on the login toggle so it's never a mystery. */
export const LIVE_ORIGIN_LABEL: string =
  VITE_ENV.VITE_LIVE_ORIGIN_LABEL ?? 'ap1.mira.bloo.io'

/**
 * A client with no session and no 401 hook.
 *
 * `/lc/users/handoff/redeem` is called from an origin that has no session yet —
 * that is the entire point of the handoff — so there is no SSID to send. It
 * must also not share `client`'s `onUnauthorized`: redeem answers a spent or
 * expired ticket with a genuine 401, which is the normal, expected failure of
 * a link someone refreshed. Letting the shared hook fire on it wipes the
 * session and hard-navigates to login before the handoff route can say what
 * actually happened.
 */
const anonymousClient = createClient({ baseUrl: API_BASE_URL })

/**
 * Session handoff, both directions (docs/session-handoff.md). Separate from
 * authApi because it authenticates nobody: it carries the session that already
 * exists across an origin boundary.
 */
export const handoffApi = createHandoffApi(client, anonymousClient)
